import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ModalService, ModalSettings, ToastService } from 'mbs-ui-kit';
import { noop, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import { getTilesConfig } from 'wizards/onboarding/onboarding.helpers';
import { OnboardingMode } from 'wizards/services/onboarding/onboarding.constants';
import { OnboardingService } from 'wizards/services/onboarding/onboarding.service';
import { FinishPath, OnboardingButtonState, OnboardingView } from './onboarding.constants';

SwiperCore.use([Navigation, Pagination]);

@UntilDestroy()
@Component({
  selector: 'mbs-onboarding-wizard',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss']
})
export class OnboardingWizardComponent implements OnInit {
  @Input() mode: OnboardingMode;
  @Output() criticalError = new EventEmitter<boolean>();

  readonly view = OnboardingView;
  readonly tilesConfig = getTilesConfig(this.i18N);
  readonly finishPath = FinishPath;

  public back: EventEmitter<Record<string, unknown>> = new EventEmitter<Record<string, unknown>>();
  public next: EventEmitter<Record<string, unknown>> = new EventEmitter<Record<string, unknown>>();
  public backButtonState: OnboardingButtonState = { disable: false, visible: true, text: '' };
  public nextButtonState: OnboardingButtonState = { disable: false, visible: true, text: '' };
  public selectedTile: OnboardingView;
  public selectedView: OnboardingView = null;
  public step = null;
  public currentStep;
  public loading = true;

  constructor(
    private onboardingService: OnboardingService,
    private i18N: I18NextPipe,
    private toastService: ToastService,
    private modalService: ModalService
  ) {}

  ngOnInit() {
    const flow = localStorage.getItem('onboardingFlow') as OnboardingView;
    this.step = localStorage.getItem('onboardingStep');

    this.onboardingService.hasError$.pipe(untilDestroyed(this)).subscribe((hasError: boolean) => {
      hasError && this.criticalError.emit(true);
    });

    this.onboardingService
      .initialize()
      .pipe(
        untilDestroyed(this),
        switchMap(() =>
          this.onboardingService.setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone).pipe(catchError(() => of(null)))
        )
      )
      .subscribe({
        next: () => {
          this.loading = false;

          if (Object.values(OnboardingView).includes(flow)) {
            this.selectedView = flow;

            localStorage.removeItem('onboardingFlow');
            localStorage.removeItem('onboardingStep');
          }
        },
        error: () => this.onboardingService.hasError$.next(true)
      });
  }

  public skip(redirectUri: string): void {
    const modalSettings: ModalSettings = {
      header: {
        title: this.i18N.transform('wizards:onboarding.buttons.skip_wizard', { format: 'title' })
      },
      footer: { okButton: { text: this.i18N.transform('wizards:onboarding.buttons.skip') } }
    };

    this.modalService
      .open(modalSettings, this.i18N.transform('wizards:onboarding.modals.skipWizard'))
      .then((result) => result && this.finish(redirectUri))
      .catch(noop);
  }

  public onViewChoiceButtonClick(view: OnboardingView): void {
    this.selectedView = view;
  }

  public moveBack(): void {
    const event = { prev: null };

    this.back.emit(event);

    if (!event.prev) {
      this.selectedView = null;
      this.backButtonState = { disable: false, visible: true, text: '' };
      this.nextButtonState = { disable: false, visible: true, text: '' };
    }
  }

  public moveNext(): void {
    this.next.emit({});
  }

  public updateButton(button: 'back' | 'next', state: Partial<OnboardingButtonState>): void {
    queueMicrotask(() => {
      (this[`${button}ButtonState`] as OnboardingButtonState) = { ...(this[`${button}ButtonState`] as OnboardingButtonState), ...state };
    });
  }

  public finish(redirectUri: string): void {
    this.onboardingService.complete().subscribe({
      next: () => {
        window.location.href = redirectUri;
      },
      error: () => {
        this.toastService.error(
          `${this.i18N.transform('shared:error.type.critical.title')} ${this.i18N.transform('shared:error.type.critical.description')}`
        );

        this.onboardingService.stable$.next(true);
      }
    });
  }
}
